import React, { useState, useEffect } from 'react';
import { Gift, Plus, Trash2, Users, Package, Star } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useRewards, useRedemptions } from '../../hooks/useRewards';
import { useCustomers } from '../../hooks/useCustomers';
import { pointsService } from '../../services/pointsService';
import LoadingSpinner from '../common/LoadingSpinner';

const CompanyRewardsOverview = () => {
  const { user } = useAuth();
  const { rewards, loading: rewardsLoading, fetchRewards } = useRewards(user?.companyId);
  const { redemptions, loading: redemptionsLoading, fetchCompanyRedemptions } = useRedemptions();
  const { customers, fetchCustomers } = useCustomers();
  
  const [stats, setStats] = useState({
    totalRewards: 0,
    activeRewards: 0,
    pendingRedemptions: 0,
    completedRedemptions: 0,
    completedAutomaticRedemptions: 0,
    completedManualRedemptions: 0,
    forceRedemptions: 0,
    totalCustomers: 0
  });

  useEffect(() => {
    if (user?.companyId) {
      loadData();
    }
  }, [user?.companyId]);

  const loadData = async () => {
    try {
      await Promise.all([
        fetchRewards(false), // Buscar todos os prêmios (ativos e inativos)
        fetchCompanyRedemptions(user.companyId),
        fetchCustomers()
      ]);
      
      // Buscar resgates forçados do histórico de pontos
      await loadForceRedemptions();
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const loadForceRedemptions = async () => {
    try {
      const pointsHistory = await pointsService.getByCompany(user.companyId);
      const forceRedemptions = pointsHistory.filter(h => 
        h.type === 'redeem' && 
        (h.reason?.includes('Resgate manual') || h.reason?.includes('Resgate forçado'))
      );
      
      setStats(prev => ({
        ...prev,
        forceRedemptions: forceRedemptions.length
      }));
    } catch (error) {
      console.error('Erro ao carregar resgates forçados:', error);
    }
  };

  useEffect(() => {
    if (rewards && redemptions && customers) {
      const completedRedemptions = redemptions.filter(r => r.status === 'completed');
      
      // Separar resgates automáticos (via prêmios) dos manuais/forçados
      const automaticRedemptions = completedRedemptions.filter(r => r.rewardId && !r.notes?.includes('Resgate forçado'));
      const manualRedemptions = completedRedemptions.filter(r => !r.rewardId || r.notes?.includes('Resgate forçado') || r.notes?.includes('Resgate manual'));
      
      setStats(prev => ({
        ...prev,
        totalRewards: rewards.length,
        activeRewards: rewards.filter(r => r.isActive).length,
        pendingRedemptions: redemptions.filter(r => r.status === 'pending').length,
        completedRedemptions: completedRedemptions.length,
        completedAutomaticRedemptions: automaticRedemptions.length,
        completedManualRedemptions: manualRedemptions.length,
        totalCustomers: customers.length
      }));
    }
  }, [rewards, redemptions, customers]);

  if (rewardsLoading || redemptionsLoading) {
    return <LoadingSpinner />;
  }

  const recentRedemptions = redemptions
    .sort((a, b) => (b.requestedAt?.seconds || 0) - (a.requestedAt?.seconds || 0))
    .slice(0, 5);

  const topRewards = rewards
    .filter(r => r.isActive)
    .sort((a, b) => a.pointsRequired - b.pointsRequired)
    .slice(0, 3);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Gift className="mr-2" />
          Visão Geral - Prêmios e Resgates
        </h1>
        <p className="text-gray-600 mt-1">
          Acompanhe o desempenho do seu programa de fidelidade
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Gift className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Prêmios</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalRewards}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Star className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Prêmios Ativos</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeRewards}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Package className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Resgates Pendentes</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingRedemptions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Package className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Resgates Concluídos</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completedRedemptions + stats.forceRedemptions}</p>
              <div className="flex text-xs text-gray-500 mt-1 space-x-2">
                <span>Auto: {stats.completedAutomaticRedemptions}</span>
                <span>Manual: {stats.completedManualRedemptions}</span>
                <span>Forçado: {stats.forceRedemptions}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Users className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Clientes</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCustomers}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Prêmios Mais Populares */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Prêmios Ativos</h2>
            <p className="text-sm text-gray-600 mt-1">Prêmios com menor exigência de pontos</p>
          </div>
          <div className="p-6">
            {topRewards.length === 0 ? (
              <div className="text-center py-8">
                <Gift className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">Nenhum prêmio ativo</p>
                <p className="text-sm text-gray-500 mt-1">Crie prêmios para motivar seus clientes</p>
              </div>
            ) : (
              <div className="space-y-4">
                {topRewards.map((reward) => (
                  <div key={reward.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{reward.name}</h3>
                      <p className="text-sm text-gray-600">{reward.description}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold text-blue-600">{reward.pointsRequired}</span>
                      <p className="text-xs text-gray-500">pontos</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Resgates Recentes */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Resgates Recentes</h2>
            <p className="text-sm text-gray-600 mt-1">Últimas solicitações de resgate</p>
          </div>
          <div className="p-6">
            {recentRedemptions.length === 0 ? (
              <div className="text-center py-8">
                <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">Nenhum resgate ainda</p>
                <p className="text-sm text-gray-500 mt-1">Os resgates aparecerão aqui</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentRedemptions.map((redemption) => (
                  <div key={redemption.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{redemption.rewardName}</h3>
                      <p className="text-sm text-gray-600">
                        {redemption.pointsUsed} pontos • {' '}
                        {redemption.requestedAt?.toDate?.()?.toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        redemption.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        redemption.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                        redemption.status === 'completed' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {redemption.status === 'pending' ? 'Pendente' :
                         redemption.status === 'approved' ? 'Aprovado' :
                         redemption.status === 'completed' ? 'Concluído' : 'Rejeitado'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Estatísticas Detalhadas de Resgates */}
      {(stats.completedRedemptions + stats.forceRedemptions) > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <Package className="mr-2" />
              Detalhamento dos Resgates Concluídos
            </h2>
            <p className="text-sm text-gray-600 mt-1">Análise dos tipos de resgates processados</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Total */}
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-3xl font-bold text-purple-600">{stats.completedRedemptions + stats.forceRedemptions}</div>
                <div className="text-sm text-purple-800 font-medium mt-1">Total de Resgates</div>
                <div className="text-xs text-purple-600 mt-1">Concluídos</div>
              </div>

              {/* Automáticos */}
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-3xl font-bold text-blue-600">{stats.completedAutomaticRedemptions}</div>
                <div className="text-sm text-blue-800 font-medium mt-1">Automáticos</div>
                <div className="text-xs text-blue-600 mt-1">
                  {(stats.completedRedemptions + stats.forceRedemptions) > 0 
                    ? `${Math.round((stats.completedAutomaticRedemptions / (stats.completedRedemptions + stats.forceRedemptions)) * 100)}%`
                    : '0%'
                  }
                </div>
              </div>

              {/* Manuais */}
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-3xl font-bold text-orange-600">{stats.completedManualRedemptions}</div>
                <div className="text-sm text-orange-800 font-medium mt-1">Manuais</div>
                <div className="text-xs text-orange-600 mt-1">
                  {(stats.completedRedemptions + stats.forceRedemptions) > 0 
                    ? `${Math.round((stats.completedManualRedemptions / (stats.completedRedemptions + stats.forceRedemptions)) * 100)}%`
                    : '0%'
                  }
                </div>
              </div>

              {/* Forçados */}
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-3xl font-bold text-red-600">{stats.forceRedemptions}</div>
                <div className="text-sm text-red-800 font-medium mt-1">Forçados</div>
                <div className="text-xs text-red-600 mt-1">
                  {(stats.completedRedemptions + stats.forceRedemptions) > 0 
                    ? `${Math.round((stats.forceRedemptions / (stats.completedRedemptions + stats.forceRedemptions)) * 100)}%`
                    : '0%'
                  }
                </div>
              </div>
            </div>

            {/* Legenda */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">Tipos de Resgate:</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-start space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mt-1 flex-shrink-0"></div>
                  <div>
                    <div className="font-medium text-gray-900">Automáticos</div>
                    <div className="text-gray-600">Cliente solicita via consulta pública → Empresa aprova → Empresa marca como entregue</div>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mt-1 flex-shrink-0"></div>
                  <div>
                    <div className="font-medium text-gray-900">Manuais</div>
                    <div className="text-gray-600">Empresa cria resgate de prêmio específico via sistema e marca como entregue</div>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full mt-1 flex-shrink-0"></div>
                  <div>
                    <div className="font-medium text-gray-900">Forçados</div>
                    <div className="text-gray-600">Empresa desconta pontos diretamente usando "Forçar Resgate" (emergência, entregas manuais)</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyRewardsOverview;
